import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDynamicPTSProjects, getKuntoarviovalikot, createDynamicPTSProject, deleteDynamicPTSProject } from '../api/api';
import { uniqueProjectName } from '../utils/subsectionUtils';
import { defaultProjectName } from './report/blocks/julkisivuV2/services/useKuntoarviovalikkoV2';
import KuntoarvioWorkspaceV2 from './KuntoarvioWorkspaceV2';
import AddNameModal from './common/AddNameModal';

function PtsProjectPanel({ pts, ptsProjects, card }) {
  const [activePtsProject, setActivePtsProject] = useState(pts);
  const activePtsIdRef = useRef(pts.id);
  const ptsProjectsRef = useRef(ptsProjects);
  ptsProjectsRef.current = ptsProjects;

  // DynamicPTSTaulu (rendered inside KuntoarvioWorkspaceV2 now, behind its
  // "PTS" tab) can itself switch to a different PTS project — this mirrors
  // that back into which project this panel/the workspace is showing.
  const handlePtsProjectChange = useCallback((newData) => {
    const newPtsId = newData?.projectId;
    if (newPtsId && newPtsId !== activePtsIdRef.current) {
      activePtsIdRef.current = newPtsId;
      setActivePtsProject(ptsProjectsRef.current.find(p => p.id === newPtsId) ?? { id: newPtsId, name: null });
    }
  }, []);

  return (
    <div className="px-1 py-2">
      <KuntoarvioWorkspaceV2 id_kiinteisto={card?.id_kiinteisto ?? null} ptsProjectId={activePtsProject.id}
        onPtsProjectChange={handlePtsProjectChange} />
    </div>
  );
}

export default function KuntotutkimuksetTab({ card }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ptsProjects, setPtsProjects] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [showDeleteProject, setShowDeleteProject] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deletingProject, setDeletingProject] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);

  // Keeps `tab`/`subtab` (and anything else already in the URL) untouched
  // — only the `project` param is added/changed, via the functional form
  // of setSearchParams rather than replacing the whole query string.
  const selectProject = useCallback((id) => {
    setActiveTab(String(id));
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('project', String(id));
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    const id = card?.id_kiinteisto;
    if (!id) return;
    setLoading(true);
    getDynamicPTSProjects(id)
      .then(async res => {
        const all = res.data ?? [];
        // Only list PTS projects that actually have V2 kuntoarviovalikko
        // data — this page used to show every dynamic PTS project
        // (including ones only ever used for the old V1 blocks, which have
        // nothing for the V2 workspace below to show), which cluttered the
        // picker with projects that just land on an empty V2 view.
        const hasV2 = await Promise.all(
          all.map(p => getKuntoarviovalikot(p.id).then(r => (r.data || []).length > 0).catch(() => false))
        );
        const projects = all.filter((_, i) => hasV2[i]);
        setPtsProjects(projects);
        if (projects.length > 0) {
          // Prefer the project named in the URL (so reloading or sharing
          // the link reopens the same one) if it's still in the filtered
          // list; otherwise fall back to the last one, same as before —
          // and write it into the URL either way, so the very first load
          // (not just a later manual tab click) also ends up with
          // `&project=<id>` rather than leaving it out until a click.
          const fromUrl = searchParams.get('project');
          const match = fromUrl && projects.find(p => String(p.id) === fromUrl);
          const chosenId = (match ?? projects[projects.length - 1]).id;
          setActiveTab(String(chosenId));
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('project', String(chosenId));
            return next;
          }, { replace: true });
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load PTS projects:', err);
        setError('Projektien lataus epäonnistui.');
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id_kiinteisto]);

  if (loading) return <div className="p-4 text-muted">Ladataan...</div>;
  if (error)   return <div className="p-4 text-danger">{error}</div>;

  const submitAddProject = async () => {
    const name = newProjectName.trim();
    if (!name || !card?.id_kiinteisto) return;
    setShowAddProject(false);
    try {
      const r = await createDynamicPTSProject(card.id_kiinteisto, name, new Date().getFullYear());
      // Added directly rather than re-running the "has V2 data" filter —
      // this project was just created explicitly for V2 use, so it
      // shouldn't have to prove it has V2 data before it can even be
      // selected (it won't, until a tarkastuskohde is added to it).
      setPtsProjects(prev => [...prev, r.data]);
      selectProject(r.data.id);
    } catch (e) {
      console.error('Failed to create PTS project:', e);
      setError('Projektin luonti epäonnistui.');
    }
  };

  const activeProject = ptsProjects.find(p => String(p.id) === activeTab) ?? null;

  // Same typed-confirmation safety gate DynamicPTSTaulu's own "Poista
  // projekti" already uses (type the exact project name) — this deletes
  // the project AND everything under it (kuntoarviovalikko, tarkastus-
  // kohteet, rakenneosat, samples, tests), so it needs the same bar here.
  const submitDeleteProject = async () => {
    if (!activeProject) return;
    setDeletingProject(true);
    try {
      await deleteDynamicPTSProject(activeProject.id);
      const remaining = ptsProjects.filter(p => p.id !== activeProject.id);
      setPtsProjects(remaining);
      setShowDeleteProject(false);
      setDeleteConfirmInput('');
      if (remaining.length > 0) {
        selectProject(remaining[remaining.length - 1].id);
      } else {
        setActiveTab(null);
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete('project');
          return next;
        }, { replace: true });
      }
    } catch (e) {
      console.error('Failed to delete PTS project:', e);
      setError('Projektin poisto epäonnistui.');
    } finally {
      setDeletingProject(false);
    }
  };

  return (
    <div style={{ maxWidth: 1400 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px 6px' }}>
        <span style={{ fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
          Projekti:
        </span>

        {/* Pill switcher (replaces the old flat tab row, which wrapped
            across multiple lines once there were more than a handful of
            projects) — one dropdown regardless of how many projects exist,
            with create/delete as separate small icon buttons beside it
            rather than mixed into the project list itself. */}
        {ptsProjects.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setShowProjectMenu(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', fontSize: '0.82rem', fontWeight: 700, border: '1px solid #198754', borderRadius: 20, background: '#f0fdf4', color: '#15803d', cursor: 'pointer' }}>
              {activeProject ? (activeProject.name || `PTS ${activeProject.id}`) : '—'} ▾
            </button>
            {showProjectMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setShowProjectMenu(false)} />
                <div style={{ position: 'absolute', top: 38, left: 0, width: 260, maxHeight: 320, overflowY: 'auto', background: '#fff', border: '1px solid #e2e4e7', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 6, zIndex: 11 }}>
                  {ptsProjects.map(pts => (
                    <button key={pts.id} type="button" onClick={() => { selectProject(pts.id); setShowProjectMenu(false); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: '0.8rem', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: activeTab === String(pts.id) ? '#f0fdf4' : 'transparent',
                        color: activeTab === String(pts.id) ? '#15803d' : '#212529',
                        fontWeight: activeTab === String(pts.id) ? 600 : 400,
                      }}>
                      {pts.name || `PTS ${pts.id}`}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <button type="button" onClick={() => { setNewProjectName(uniqueProjectName(defaultProjectName('kuntoarvio'), ptsProjects)); setShowAddProject(true); }}
          title="Uusi projekti"
          style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid #198754', background: '#fff', color: '#198754', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          +
        </button>
        {activeProject && (
          <button type="button" onClick={() => { setDeleteConfirmInput(''); setShowDeleteProject(true); }} title="Poista projekti"
            style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid #dc3545', background: '#fff', color: '#dc3545', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="bi bi-trash" />
          </button>
        )}
      </div>

      {ptsProjects.length === 0 ? (
        <div className="p-4 text-muted">Ei V2-kuntoarviovalikkoja tälle kiinteistölle. Luo uusi projekti aloittaaksesi.</div>
      ) : (
        ptsProjects.map(pts =>
          activeTab === String(pts.id) ? (
            <PtsProjectPanel key={pts.id} pts={pts} ptsProjects={ptsProjects} card={card} />
          ) : null
        )
      )}

      {showAddProject && (
        <AddNameModal title="Uusi kuntotutkimusprojekti" placeholder="esim. Kuntoarvio 2026"
          value={newProjectName} onChange={setNewProjectName} onSubmit={submitAddProject} onClose={() => setShowAddProject(false)} />
      )}

      {showDeleteProject && activeProject && (() => {
        const expectedName = activeProject.name || `PTS ${activeProject.id}`;
        const matches = deleteConfirmInput === expectedName;
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => { if (!deletingProject) { setShowDeleteProject(false); setDeleteConfirmInput(''); } }}>
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(420px, 95vw)', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}
              onClick={e => e.stopPropagation()}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#dc3545' }}>Poista projekti</div>
              <div style={{ fontSize: '0.85rem', color: '#495057' }}>
                Tämä poistaa projektin <strong>{expectedName}</strong> ja kaikki sen tiedot pysyvästi
                (kuntoarviovalikko, tarkastuskohteet, rakenneosat, näytteet, testit). Toimintoa ei voi perua.
              </div>
              <div>
                <div style={{ fontSize: '0.74rem', color: '#6c757d', marginBottom: 6 }}>Vahvista kirjoittamalla projektin nimi:</div>
                <input type="text" autoFocus placeholder={expectedName} value={deleteConfirmInput}
                  onChange={e => setDeleteConfirmInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && matches && !deletingProject) submitDeleteProject(); if (e.key === 'Escape') setShowDeleteProject(false); }}
                  style={{ width: '100%', border: '1.5px solid #ced4da', borderRadius: 6, padding: '8px 12px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" disabled={deletingProject} onClick={() => { setShowDeleteProject(false); setDeleteConfirmInput(''); }} className="btn btn-sm btn-outline-secondary">
                  Peruuta
                </button>
                <button type="button" disabled={!matches || deletingProject} onClick={submitDeleteProject}
                  style={{ border: 'none', background: '#dc3545', color: '#fff', borderRadius: 4, padding: '7px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: (matches && !deletingProject) ? 'pointer' : 'not-allowed', opacity: (matches && !deletingProject) ? 1 : 0.6 }}>
                  {deletingProject ? 'Poistetaan…' : 'Poista projekti'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
